import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Search,
  Trash2,
  Edit3,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Users as UsersIcon,
  Sparkles,
  Download,
  Star,
  X as XIcon,
  Boxes,
  ChevronDown as CaretDown,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/AppLayout";
import AssignBatchModal from "@/components/questions/AssignBatchModal";
import AutoBatchModal from "@/components/questions/AutoBatchModal";
import EditQuestionModal from "@/components/questions/EditQuestionModal";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { tagChipStyle, buildTagColorMap } from "@/lib/tagColors";
import { downloadExport } from "@/lib/exportHelpers";

const STATUS_COLORS = {
  unassigned: "bg-slate-100 text-slate-600",
  batched: "bg-indigo-50 text-indigo-700",
  assigned: "bg-blue-50 text-blue-700",
  saved: "bg-amber-50 text-amber-700",
  submitted: "bg-purple-50 text-purple-700",
  approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-rose-50 text-rose-700",
  in_review: "bg-amber-50 text-amber-700",
  reviewed: "bg-emerald-50 text-emerald-700",
};

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "unassigned", label: "Unassigned" },
  { value: "batched", label: "Batched" },
  { value: "assigned", label: "Assigned" },
  { value: "saved", label: "Saved (draft)" },
  { value: "submitted", label: "Submitted" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, "all"];
const ALL_PAGE_SIZE = 1000;

const ADMIN_ROLES = ["super_admin", "admin"];

export default function RawQuestions() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [subject, setSubject] = useState("");
  const [rating, setRating] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [batchedFilter, setBatchedFilter] = useState(""); // ""=any, "yes"=in batch, "no"=unbatched
  const [sort, setSort] = useState("uploaded_at");
  const [direction, setDirection] = useState("desc");
  const [loading, setLoading] = useState(false);

  const [subjects, setSubjects] = useState([]);
  const [tags, setTags] = useState([]);

  const [selected, setSelected] = useState(new Set());
  const [assignOpen, setAssignOpen] = useState(false);
  const [autoBatchOpen, setAutoBatchOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  // Load subjects + tags once for filter dropdowns
  useEffect(() => {
    (async () => {
      try {
        const [{ data: s }, { data: t }] = await Promise.all([
          api.get("/questions/subjects"),
          api.get("/tags"),
        ]);
        setSubjects(s);
        setTags(t);
      } catch (e) {
        console.error("Failed to load Raw Questions filter options:", e);
        /* ignore — filters just stay empty */
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        sort,
        direction,
      };
      if (search) params.search = search;
      if (status) params.status = status;
      if (subject) params.subject = subject;
      if (rating) params.rating = Number(rating);
      if (tagFilter) params.tags = tagFilter;
      if (batchedFilter === "yes") params.batched = true;
      else if (batchedFilter === "no") params.batched = false;
      const { data } = await api.get("/questions", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, status, subject, rating, tagFilter, batchedFilter, sort, direction]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  const onDelete = (q) => {
    setConfirmState({
      title: "Delete this question?",
      message: (
        <span>
          You're about to permanently delete <strong>ID {q.source_id || "—"}</strong>:
          <br />
          <em className="text-slate-500">
            {q.question_text?.slice(0, 140)}
            {q.question_text && q.question_text.length > 140 ? "…" : ""}
          </em>
        </span>
      ),
      confirmLabel: "Delete",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/questions/${q.id}`);
          toast.success("Question deleted");
          load();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
      },
    });
  };

  const onBulkDelete = () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setConfirmState({
      title: `Delete ${ids.length} question${ids.length === 1 ? "" : "s"}?`,
      message: (
        <span>
          You're about to permanently delete <strong>{ids.length}</strong> selected question
          {ids.length === 1 ? "" : "s"}. This cannot be undone.
        </span>
      ),
      confirmLabel: `Delete ${ids.length}`,
      danger: true,
      onConfirm: async () => {
        try {
          const { data } = await api.post("/questions/bulk-delete", { question_ids: ids });
          toast.success(`Deleted ${data.deleted} question${data.deleted === 1 ? "" : "s"}`);
          setSelected(new Set());
          load();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
      },
    });
  };

  const selectedBatchedCount = useMemo(
    () => items.filter((q) => selected.has(q.id) && q.batch_id).length,
    [items, selected]
  );

  const onBulkUnbatch = () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setConfirmState({
      title: `Unbatch ${ids.length} question${ids.length === 1 ? "" : "s"}?`,
      message: (
        <span>
          Selected questions will be removed from their batches and returned to the raw pool.
          Approved or rejected ones are skipped automatically.
        </span>
      ),
      confirmLabel: `Unbatch ${ids.length}`,
      danger: true,
      onConfirm: async () => {
        try {
          const { data } = await api.post("/questions/bulk-unbatch", { question_ids: ids });
          toast.success(`Unbatched ${data.unbatched} question${data.unbatched === 1 ? "" : "s"}`);
          if (data.skipped > 0) toast.warning(`Skipped ${data.skipped} (already QC'd)`);
          setSelected(new Set());
          load();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
      },
    });
  };

  const onExport = (mode = "new") => downloadExport(mode);

  const onHeaderClick = (field) => {
    if (sort === field) {
      setDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(field);
      setDirection("asc");
    }
  };

  const clearFilters = () => {
    setStatus("");
    setSubject("");
    setRating("");
    setTagFilter("");
    setBatchedFilter("");
    setSearch("");
    setPage(1);
  };

  const hasFilters = status || subject || rating || tagFilter || batchedFilter || search;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const tagColorMap = useMemo(() => buildTagColorMap(tags), [tags]);

  return (
    <PageContainer>
      <PageHeader
        title="Raw Questions"
        subtitle={`${total} question${total === 1 ? "" : "s"} in the database`}
        actions={
          isAdmin ? (
            <div className="flex flex-wrap items-center gap-2">
              {selected.size > 0 && (
                <>
                  <span
                    data-testid="selected-count-pill"
                    className="inline-flex items-center px-2.5 py-1 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs font-mono font-semibold"
                  >
                    {selected.size} selected
                  </span>
                  <button
                    data-testid="assign-batch-btn"
                    onClick={() => setAssignOpen(true)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition"
                  >
                    <UsersIcon className="w-4 h-4" />
                    Assign
                  </button>
                  {selectedBatchedCount > 0 && (
                    <button
                      data-testid="bulk-unbatch-btn"
                      onClick={onBulkUnbatch}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-semibold transition"
                      title={`Unbatch ${selectedBatchedCount} selected`}
                    >
                      <Boxes className="w-4 h-4" /> Unbatch ({selectedBatchedCount})
                    </button>
                  )}
                  <button
                    data-testid="bulk-delete-questions-btn"
                    onClick={onBulkDelete}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-sm font-semibold transition"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </>
              )}
              <button
                data-testid="auto-batch-btn"
                onClick={() => setAutoBatchOpen(true)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-orange-200 text-orange-700 hover:bg-orange-50 text-sm font-semibold transition"
              >
                <Sparkles className="w-4 h-4" />
                Auto-Batch
              </button>
              <ExportMenu onExport={onExport} />
            </div>
          ) : null
        }
      />

      {/* Filters row 1: search + page size */}
      <div className="bg-white rounded-xl border border-slate-100 brand-shadow p-3 mb-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="questions-search"
            type="text"
            placeholder="Search question text…"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-xs font-mono text-slate-500">
          Per page
          <select
            data-testid="questions-page-size"
            value={pageSize === ALL_PAGE_SIZE ? "all" : String(pageSize)}
            onChange={(e) => {
              const v = e.target.value;
              setPage(1);
              setPageSize(v === "all" ? ALL_PAGE_SIZE : Number(v));
            }}
            className="px-2 py-1.5 rounded-md border border-slate-200 text-sm bg-white outline-none focus:border-orange-500"
          >
            {PAGE_SIZE_OPTIONS.map((o) => (
              <option key={String(o)} value={o === "all" ? "all" : String(o)}>
                {o === "all" ? "All" : o}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Filters row 2: status / subject / rating / tag */}
      <div className="bg-white rounded-xl border border-slate-100 brand-shadow p-3 mb-5 flex flex-wrap items-center gap-2">
        <select
          data-testid="questions-status-filter"
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value || "all"} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <select
          data-testid="questions-subject-filter"
          value={subject}
          onChange={(e) => {
            setPage(1);
            setSubject(e.target.value);
          }}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none max-w-[260px]"
        >
          <option value="">All subjects</option>
          {subjects.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          data-testid="questions-rating-filter"
          value={rating}
          onChange={(e) => {
            setPage(1);
            setRating(e.target.value);
          }}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
        >
          <option value="">All ratings</option>
          {[5, 4, 3, 2, 1].map((n) => (
            <option key={n} value={n}>
              {n} ★ only
            </option>
          ))}
        </select>

        <select
          data-testid="questions-tag-filter"
          value={tagFilter}
          onChange={(e) => {
            setPage(1);
            setTagFilter(e.target.value);
          }}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none max-w-[260px]"
        >
          <option value="">All tags</option>
          {tags.map((t) => (
            <option key={t.id} value={t.name}>
              {t.name}
            </option>
          ))}
        </select>

        <select
          data-testid="questions-batched-filter"
          value={batchedFilter}
          onChange={(e) => {
            setPage(1);
            setBatchedFilter(e.target.value);
          }}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
        >
          <option value="">Any batch</option>
          <option value="no">Unbatched only</option>
          <option value="yes">In a batch</option>
        </select>

        {hasFilters && (
          <button
            data-testid="questions-clear-filters"
            onClick={clearFilters}
            className="inline-flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-mono text-slate-600 hover:bg-slate-50"
          >
            <XIcon className="w-3 h-3" /> Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-100 brand-shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-slate-500">
                {isAdmin && (
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={items.length > 0 && selected.size === items.length}
                      onChange={toggleAll}
                      className="rounded border-slate-300 text-orange-500 focus:ring-orange-500/20"
                    />
                  </th>
                )}
                <SortableTh field="source_id" sort={sort} direction={direction} onClick={onHeaderClick} className="w-16">ID</SortableTh>
                <SortableTh field="question_text" sort={sort} direction={direction} onClick={onHeaderClick} className="min-w-[280px]">Question</SortableTh>
                <SortableTh field="subject" sort={sort} direction={direction} onClick={onHeaderClick}>Subject</SortableTh>
                <SortableTh field="category" sort={sort} direction={direction} onClick={onHeaderClick}>Category</SortableTh>
                <SortableTh field="difficulty" sort={sort} direction={direction} onClick={onHeaderClick}>Difficulty</SortableTh>
                <SortableTh field="raw_rating" sort={sort} direction={direction} onClick={onHeaderClick}>Raw Rating</SortableTh>
                <SortableTh field="rating" sort={sort} direction={direction} onClick={onHeaderClick}>Rating</SortableTh>
                <th className="px-4 py-3">Tag</th>
                <th className="px-4 py-3">Batch</th>
                <SortableTh field="status" sort={sort} direction={direction} onClick={onHeaderClick}>Status</SortableTh>
                <SortableTh field="reviewer_name" sort={sort} direction={direction} onClick={onHeaderClick}>Reviewer</SortableTh>
                <th className="px-4 py-3 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={13} className="px-4 py-10 text-center text-sm text-slate-400">
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-4 py-10 text-center text-sm text-slate-400">
                    No questions match these filters.
                  </td>
                </tr>
              ) : (
                items.map((q) => (
                  <QuestionRow
                    key={q.id}
                    q={q}
                    isAdmin={isAdmin}
                    selected={selected.has(q.id)}
                    onToggle={toggleSelect}
                    onEdit={setEditing}
                    onDelete={onDelete}
                    tagColors={tagColorMap}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500 font-mono">
          <div>
            Page {page} of {pages} · {total} total · showing {items.length}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page === pages}
              className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {assignOpen && (
        <AssignBatchModal
          selectedIds={Array.from(selected)}
          onClose={() => setAssignOpen(false)}
          onAssigned={() => {
            setSelected(new Set());
            setAssignOpen(false);
            load();
          }}
        />
      )}

      {autoBatchOpen && (
        <AutoBatchModal
          onClose={() => setAutoBatchOpen(false)}
          onCreated={() => {
            setAutoBatchOpen(false);
            load();
          }}
        />
      )}

      {editing && (
        <EditQuestionModal
          question={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </PageContainer>
  );
}

function SortableTh({ field, sort, direction, onClick, className = "", children }) {
  const active = sort === field;
  return (
    <th className={`px-4 py-3 ${className}`}>
      <button
        type="button"
        onClick={() => onClick(field)}
        data-testid={`sort-th-${field}`}
        className={`inline-flex items-center gap-1 uppercase tracking-wider font-mono text-[11px] transition ${
          active ? "text-orange-600" : "text-slate-500 hover:text-slate-900"
        }`}
      >
        {children}
        {active ? (
          direction === "asc" ? (
            <ChevronUp className="w-3 h-3" />
          ) : (
            <ChevronDown className="w-3 h-3" />
          )
        ) : (
          <ChevronUp className="w-3 h-3 opacity-0 group-hover:opacity-40" />
        )}
      </button>
    </th>
  );
}

function QuestionRow({ q, isAdmin, selected, onToggle, onEdit, onDelete, tagColors = {} }) {
  return (
    <tr
      data-testid={`question-row-${q.id}`}
      className="border-b border-slate-50 hover:bg-slate-50/60 transition"
    >
      {isAdmin && (
        <td className="px-4 py-3">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggle(q.id)}
            data-testid={`question-select-${q.id}`}
            className="rounded border-slate-300 text-orange-500 focus:ring-orange-500/20"
          />
        </td>
      )}
      <td className="px-4 py-3 font-mono text-xs text-slate-500">{q.source_id || "—"}</td>
      <td className="px-4 py-3 max-w-[420px]">
        <div className="line-clamp-2 text-slate-800">{q.question_text}</div>
        {q.exam && (
          <span className="inline-block mt-1 px-1.5 py-0.5 rounded-md text-[10px] font-mono bg-slate-100 text-slate-500">
            {q.exam}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-slate-700">{q.subject || "—"}</td>
      <td className="px-4 py-3 text-slate-700">{q.category || "—"}</td>
      <td className="px-4 py-3">
        <span className="px-1.5 py-0.5 rounded-md text-[11px] font-mono bg-slate-100 text-slate-600">
          {q.difficulty || "—"}
        </span>
      </td>
      <td className="px-4 py-3">
        {q.raw_rating ? (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-xs font-mono font-semibold"
            title="Raw rating from uploaded sheet"
            data-testid={`question-raw-rating-${q.id}`}
          >
            {q.raw_rating}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        {q.rating ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-orange-50 text-orange-700 text-xs font-mono font-semibold">
            {q.rating}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1 max-w-[160px]">
          {(q.tags || []).map((t) => (
            <span
              key={t}
              style={tagChipStyle(tagColors[t])}
              className="px-1.5 py-0.5 rounded-md text-[10px] font-mono border"
            >
              {t}
            </span>
          ))}
          {(!q.tags || q.tags.length === 0) && <span className="text-slate-300">—</span>}
        </div>
      </td>
      <td className="px-4 py-3 max-w-[180px]">
        {q.batch_name ? (
          <span
            data-testid={`question-batch-${q.id}`}
            className="inline-block px-1.5 py-0.5 rounded-md text-[10px] font-mono bg-indigo-50 text-indigo-700 truncate max-w-[160px]"
            title={q.batch_name}
          >
            {q.batch_name}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <span
          className={`px-2 py-0.5 rounded-md text-[11px] font-mono uppercase tracking-wider ${
            STATUS_COLORS[q.status] || "bg-slate-100 text-slate-600"
          }`}
        >
          {q.status}
        </span>
      </td>
      <td className="px-4 py-3 text-xs text-slate-600">
        {q.reviewer_name || q.assigned_to_name || "—"}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          {isAdmin && (
            <>
              <button
                onClick={() => onEdit(q)}
                data-testid={`question-edit-btn-${q.id}`}
                className="p-2 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                title="Edit"
              >
                <Edit3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(q)}
                data-testid={`question-delete-btn-${q.id}`}
                className="p-2 rounded-md text-rose-500 hover:bg-rose-50"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function ExportMenu({ onExport }) {
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/export/preview");
        if (!cancelled) setCounts(data);
      } catch (e) {
        console.error("Failed to load export preview counts:", e);
        if (!cancelled) setCounts({ new: 0, all: 0, exported: 0 });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (!e.target.closest?.("[data-export-menu]")) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const choose = (mode) => {
    setOpen(false);
    onExport(mode);
  };

  return (
    <div className="relative" data-export-menu>
      <button
        data-testid="export-approved-btn"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-semibold transition"
      >
        <Download className="w-4 h-4" />
        Export Approved
        <CaretDown className="w-3.5 h-3.5 -ml-0.5 opacity-70" />
      </button>
      {open && (
        <div
          data-testid="export-menu"
          className="absolute right-0 mt-1.5 w-72 rounded-xl border border-slate-200 bg-white shadow-lg z-30 overflow-hidden"
        >
          <ExportOption
            label="New only"
            testid="export-mode-new"
            help="Not previously exported"
            count={counts?.new}
            onClick={() => choose("new")}
            highlight
          />
          <ExportOption
            label="All approved"
            testid="export-mode-all"
            help="Every approved question"
            count={counts?.all}
            onClick={() => choose("all")}
          />
          <ExportOption
            label="Previously exported"
            testid="export-mode-exported"
            help="Re-download past exports"
            count={counts?.exported}
            onClick={() => choose("exported")}
          />
        </div>
      )}
    </div>
  );
}

function ExportOption({ label, help, count, onClick, testid, highlight = false }) {
  const disabled = count == null || count === 0;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      disabled={disabled}
      title={count === 0 ? "Nothing to export in this mode" : undefined}
      className={`w-full flex items-start justify-between gap-3 px-4 py-3 text-left transition disabled:opacity-50 disabled:cursor-not-allowed ${
        highlight ? "hover:bg-orange-50" : "hover:bg-slate-50"
      }`}
    >
      <div className="min-w-0">
        <div className={`text-sm font-semibold ${highlight ? "text-orange-700" : "text-slate-800"}`}>
          {label}
        </div>
        <div className="text-[11px] text-slate-500">{help}</div>
      </div>
      <span className="text-xs font-mono text-slate-500 mt-0.5">
        {count == null ? "…" : count}
      </span>
    </button>
  );
}
